import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {StyledUserRoleSelectContainer, StyledUserRoleSelect} from './StyledUserRoleSelect';
import {userRolesConstants} from '../../../../../constants';
import {teamsHelpers} from '../../../../../utils/helpers';
import {alertActions} from '../../../../../state/actions/alert.actions';
import {useEmployeeEmail} from '../../../../../hooks/user/useEmployeeEmail';

const {ADMIN, BOOKKEEPER, MANAGER, SUPPORT, USER} = userRolesConstants;

const UserRoleSelect = ({
  defaultValue,
  email,
  teamIds,
  onChange,
  ...rest
}) => {
  const editorEmail = useEmployeeEmail();
  const [t] = useTranslation(['main', 'teams']);
  const [value, setValue] = useState(defaultValue || USER);

  const hasTeam = Array.isArray(teamIds) && teamIds.length > 0;

  const options = useMemo(() => {
    let options = [
      {label: t('userRoles.admin'), value: ADMIN},
      {label: t('userRoles.manager'), value: MANAGER},
      {label: t('userRoles.user'), value: USER},
      {label: t('userRoles.bookkeeper'), value: BOOKKEEPER},
      {label: t('userRoles.support'), value: SUPPORT},
    ];
    const isSupportRole = teamsHelpers.isSupport(defaultValue);
    if (!isSupportRole) options = options.filter(o => !teamsHelpers.isSupport(o.value));
    return options;
  }, [defaultValue, t]);

  const hideSelect = useMemo(() => {
    const isEditor = editorEmail === email;
    const isSupportRole = teamsHelpers.isSupport(defaultValue);
    return isEditor || isSupportRole;
  }, [defaultValue, editorEmail, email]);

  const handleOnChange = (newValue) => {
    // A Manager must belong to a team; keep the current role and surface an
    // alert when Manager is selected for an employee without a team.
    if (newValue === MANAGER && !hasTeam) {
      alertActions.error(t('teams:validation.managerRequiresTeam'));
      return;
    }
    setValue(newValue);
    onChange?.(newValue);
  };

  const option = options.find(o => o.value === value);
  const optionLabel = option?.label || '';

  return (
    <StyledUserRoleSelectContainer {...rest}>
      {
        hideSelect
          ? optionLabel
          : (
            <StyledUserRoleSelect
              onChange={handleOnChange}
              options={options}
              value={value}
              variant='outlined'
            />
          )
      }
    </StyledUserRoleSelectContainer>
  );
}

UserRoleSelect.propTypes = {
  teamIds: PropTypes.array,
  onChange: PropTypes.func,
}

export default UserRoleSelect;
